import { renderInspirationCardFromElastic } from '../InspirationCard';

export const inspirationElasticMethods = {
    canUseElastic() {
        return Boolean(this.bootstrap.elasticApiUrl);
    },

    isDesignersInspirationsView(state = this.state) {
        const type =
            state?.type ||
            this.bootstrap?.inspirationsType ||
            'produkty';

        return type === 'projektanci';
    },

    getEmptyInspirationsElasticResponse(listingState = this.state) {
        const perPage = Number(this.bootstrap.perPage || 16);
        const page = Number(listingState.page || 1);

        return {
            success: true,
            totalHits: 0,
            totalPages: 0,
            page,
            perPage,
            items: [],
        };
    },

    getContextTermSlug() {
        return '';
    },

    shouldRefetchAfterSsr(state = this.state) {
        const current = {
            page: Number(state.page || 1),
            sort: state.sort || 'popular',
            filters: state.filters || {},
            type: state.type || 'produkty',
        };
        const baseline = {
            page: Number(this.ssrState.page || 1),
            sort: this.ssrState.sort || 'popular',
            filters: this.ssrState.filters || {},
            type: this.ssrState.type || this.bootstrap.inspirationsType || 'produkty',
        };

        if (current.page !== baseline.page) {
            return true;
        }

        if ((current.sort || 'popular') !== (baseline.sort || 'popular')) {
            return true;
        }

        if ((current.type || 'produkty') !== (baseline.type || 'produkty')) {
            return true;
        }

        return (
            JSON.stringify(this.normalizeFilters(current.filters)) !==
            JSON.stringify(this.normalizeFilters(baseline.filters))
        );
    },

    async fetchListing() {
        return this.fetchListingPayload(this.state);
    },

    async fetchListingCount(listingState = this.state) {
        if (this.isDesignersInspirationsView(listingState)) {
            return 0;
        }

        if (!this.canUseElastic()) {
            return 0;
        }

        const elasticData = await this.fetchElasticListingRaw(listingState);

        return Number(elasticData.totalHits || 0);
    },

    async fetchListingPayload(listingState = this.state) {
        if (!this.canUseElastic()) {
            throw new Error(
                'Listing inspiracji wymaga skonfigurowanego ELASTIC_NODE_API.'
            );
        }

        if (this.isDesignersInspirationsView(listingState)) {
            return this.buildEmptyInspirationsListingPayload(listingState);
        }

        return this.fetchElasticListingPayload(listingState);
    },

    logElasticRequestOnce(elasticBody, listingState = this.state) {
        if (!this.bootstrap.debugElasticRequest || this.elasticRequestLogged) {
            return;
        }

        this.elasticRequestLogged = true;

        console.info('[mda-inspirations-es]', {
            source: 'js-client',
            page_url: this.bootstrap.pageUrl || window.location.href,
            request_uri: window.location.pathname + window.location.search,
            elastic_api_url: this.bootstrap.elasticApiUrl || '',
            listing_state: {
                page: listingState.page || 1,
                sort: listingState.sort || 'popular',
                filters: listingState.filters || {},
            },
            elastic_body: elasticBody,
        });
    },

    async fetchElasticListingRaw(listingState = this.state) {
        if (this.isDesignersInspirationsView(listingState)) {
            return this.getEmptyInspirationsElasticResponse(listingState);
        }

        const elasticBody = this.buildElasticRequestBody(listingState);
        this.logElasticRequestOnce(elasticBody, listingState);
        const elasticHeaders = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };

        if (this.bootstrap.elasticApiToken) {
            elasticHeaders['X-Listing-Token'] = this.bootstrap.elasticApiToken;
        }

        const elasticResponse = await fetch(this.bootstrap.elasticApiUrl, {
            method: 'POST',
            headers: elasticHeaders,
            body: JSON.stringify(elasticBody),
        });

        if (!elasticResponse.ok) {
            throw new Error('Elastic inspirations request failed.');
        }

        const elasticData = await elasticResponse.json();

        if (!elasticData?.success) {
            throw new Error('Elastic inspirations response was not successful.');
        }

        return elasticData;
    },

    buildEmptyInspirationsListingPayload(listingState = this.state) {
        const perPage = Number(this.bootstrap.perPage || 16);
        const page = Number(listingState.page || 1);

        return {
            results: [],
            total: 0,
            pagination: {
                page,
                perPage,
                current: 0,
                total: 0,
                maxPages: 1,
                hasMore: false,
            },
            activeFilterChips: this.buildActiveFilterChipsFromState(
                listingState.filters
            ),
            sort: listingState.sort,
            page,
        };
    },

    async fetchElasticListingPayload(listingState = this.state) {
        const elasticData = await this.fetchElasticListingRaw(listingState);
        const perPage = Number(
            elasticData.perPage || this.bootstrap.perPage || 16
        );
        const page = Number(elasticData.page || listingState.page || 1);
        const total = Number(elasticData.totalHits || 0);
        const maxPages = Math.max(
            Number(elasticData.totalPages || 0),
            total > 0 ? Math.ceil(total / perPage) : 1
        );
        const current = Math.min(total, page * perPage);
        const hasMore = page < maxPages;
        const elasticItems = Array.isArray(elasticData.items)
            ? elasticData.items
            : [];
        const openLabel = String(
            this.bootstrap?.i18n?.openInspiration || 'Zobacz inspirację'
        );

        const results = elasticItems
            .map(item => {
                const rendered = renderInspirationCardFromElastic(item, {
                    openLabel,
                });

                return {
                    id: rendered.id,
                    html: rendered.html,
                };
            })
            .filter(item => typeof item.html === 'string' && item.html !== '');

        return {
            results,
            total,
            pagination: {
                page,
                perPage,
                current,
                total,
                maxPages,
                hasMore,
            },
            activeFilterChips: this.buildActiveFilterChipsFromState(
                listingState.filters
            ),
            sort: listingState.sort,
            page,
        };
    },

    getFilterTaxonomies() {
        const fromBootstrap = this.bootstrap.filterTaxonomies;

        if (Array.isArray(fromBootstrap) && fromBootstrap.length > 0) {
            return fromBootstrap;
        }

        return ['room', 'style', 'pa_kolor_wiodacy'];
    },

    buildElasticRequestBody(listingState = this.state) {
        const taxonomies = this.getFilterTaxonomies();
        const requestFilters = {};
        const filterTermIds = {};

        taxonomies.forEach(taxonomy => {
            const selectedSlugs = listingState.filters?.[taxonomy];
            const hasExplicitFilterKey = Object.prototype.hasOwnProperty.call(
                listingState.filters || {},
                taxonomy
            );
            let slugs = [];

            if (Array.isArray(selectedSlugs)) {
                if (selectedSlugs.length > 0) {
                    slugs = this.expandTermSlugs(taxonomy, selectedSlugs);
                } else if (hasExplicitFilterKey) {
                    slugs = [];
                }
            } else if (selectedSlugs) {
                slugs = this.expandTermSlugs(taxonomy, [selectedSlugs]);
            }

            if (slugs.length > 0) {
                requestFilters[taxonomy] = slugs;
                const termIds = this.expandTermIds(taxonomy, slugs);

                if (termIds.length > 0) {
                    filterTermIds[taxonomy] = termIds;
                }
            }
        });

        return {
            listing: 'inspirations',
            page: listingState.page,
            perPage: this.bootstrap.perPage || 16,
            sortBy: this.mapSortToElastic(listingState.sort),
            language: this.bootstrap.language || 'pl',
            contextTaxonomy: '',
            contextTermSlug: '',
            filters: requestFilters,
            filterTermIds,
            inspirationsType:
                listingState.type ||
                this.bootstrap.inspirationsType ||
                'produkty',
        };
    },

    expandTermIds(taxonomy, slugs = []) {
        const termIndex = this.bootstrap.termIndex?.[taxonomy] || {};
        const ids = new Set();

        this.expandTermSlugs(taxonomy, slugs).forEach(slug => {
            const termId = Number(termIndex[slug]?.id || 0);

            if (termId > 0) {
                ids.add(termId);
            }
        });

        return Array.from(ids);
    },

    expandTermSlugs(taxonomy, slugs = []) {
        const descendantsMap = this.bootstrap.termDescendants?.[taxonomy] || {};
        const expanded = new Set();

        slugs.forEach(slug => {
            const normalizedSlug = String(slug || '').trim();

            if (!normalizedSlug) {
                return;
            }

            const branch = descendantsMap[normalizedSlug];

            if (Array.isArray(branch) && branch.length > 0) {
                branch.forEach(item => expanded.add(item));
                return;
            }

            expanded.add(normalizedSlug);
        });

        return Array.from(expanded);
    },

    mapSortToElastic(sort) {
        switch (sort) {
            case 'bestseller':
            case 'popular':
                return 'popular';
            case 'newest':
                return 'newest';
            case 'price_asc':
            case 'price_desc':
                return sort;
            default:
                return 'popular';
        }
    },
};
